import {compact} from '@benchling/js-utils';

import {assertAndContinue} from 'Asserts';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import OligoConjugateRegTableColumnTransformer from 'modbio/complex-polymers/oligo-conjugates/OligoConjugateRegTableColumnTransformer';
import OligoDuplexRegTableColumnTransformer from 'modbio/complex-polymers/oligo-duplexes/OligoDuplexRegTableColumnTransformer';
import {ImplementedStaticSchemaInterfaceGQL} from 'notebook/structured-tables/components/modals/RegistrationTableInsertModal';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';

export interface RegistrationTableRowData {
  bioentityExists: boolean;
}

/**
 * Base interface for classes that alter registration table columns based on the implementation of particular schema interfaces.
 *
 * Implementers should add an instantiation of their class to the COLUMN_TRANSFORMERS array.
 * Then, the `replaceExistingColumn` hook will be called to transform the default registration table columns.
 */
export interface SchemaInterfaceColumnTransformerBase {
  /**
   * Replace an existing column in the registration table with a new column based on the implemented schema interface.
   *
   * If no replacement is required, return null.
   */
  replaceExistingColumn: (
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ) => StructuredTableColumn | null;

  /**
   * Add net new columns to the registration table based on the implemented schema interface.
   */
  getNewColumns: (
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ) => Array<StructuredTableColumn>;
}

const COLUMN_TRANSFORMERS: {[interfaceId: string]: SchemaInterfaceColumnTransformerBase} = {
  [ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID.toString()]: new OligoConjugateRegTableColumnTransformer(),
  [ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID.toString()]: new OligoDuplexRegTableColumnTransformer(),
};

/**
 * Transforms the existing columns in a registration table based on the implemented schema interfaces.
 * Expects that no two interfaces will attempt to replace/transform the same column.
 * Currently, only replaces existing columns or adds net new columns.
 */
export function transformColumnsForInterfaces(
  existingColumns: Array<StructuredTableColumn>,
  implementedSchemaInterfaces: Array<ImplementedStaticSchemaInterfaceGQL>,
  tableRowData: RegistrationTableRowData
): Array<StructuredTableColumn> {
  const transformers = compact(
    implementedSchemaInterfaces.map((implementedInterface) =>
      COLUMN_TRANSFORMERS[implementedInterface.schemaInterface.id]
        ? {transformer: COLUMN_TRANSFORMERS[implementedInterface.schemaInterface.id], implementedInterface}
        : null
    )
  );
  const transformedColumns = existingColumns.map((column) => {
    const columnTransforms = compact(
      transformers.map(({transformer, implementedInterface}) =>
        transformer.replaceExistingColumn(column, implementedInterface, tableRowData)
      )
    );
    assertAndContinue(
      columnTransforms.length <= 1,
      `Multiple schema interfaces attempted to transform column ${column.name}`
    );
    if (columnTransforms.length === 1) {
      return columnTransforms[0];
    }
    return column;
  });
  const newColumns = transformers.flatMap(({transformer, implementedInterface}) =>
    transformer.getNewColumns(implementedInterface, tableRowData)
  );

  return [...transformedColumns, ...newColumns];
}
